import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';

import { MsalService } from '@azure/msal-angular';
import { Store, select } from '@ngrx/store';
import { Subscription, combineLatest, firstValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';

import { DeviceGroupSelectors } from 'src/app/features/device-group';
import { MsalAuthSelectors } from 'src/app/features/msal-auth';
import { PackageUsersSelectors } from 'src/app/features/package-users';
import { PackagesSelectors } from 'src/app/features/packages';
import { environment } from 'src/environments/environment';
import { buildCorrelationHeaders } from 'src/app/interceptors/correlation-headers';

import { AvTelemetryBufferRow } from '../models/av-telemetry-buffer.model';
import {
  AV_TELEMETRY_UPLOAD_CONFIG,
  AvTelemetryUploadConfig,
  AvTelemetryUploadResult,
} from '../models/av-telemetry-upload.model';
import { AvTelemetryBufferStore } from './av-telemetry-buffer.store';

/** `permanentFailure` is a 422 — the backend will never accept this chunk, discard it. */
export type PostOutcome = 'sent' | 'permanentFailure' | 'transientFailure';

/** One keepalive request's worth of rows, gzipped ahead of time. */
export interface TeardownBatch {
  body: Blob;
  ids: number[];
}

/**
 * Ships buffered telemetry to the ingest endpoint as gzipped NDJSON.
 *
 * Reads the store, not `flushed$`, which cannot see rows a failed POST left behind.
 */
@Injectable({ providedIn: 'root' })
export class AvTelemetryUploaderService implements OnDestroy {
  static readonly INGEST_ROUTE = 'avTelemetry';

  /** Ids shipped on teardown, which could not delete them. The next start() does. */
  static readonly TEARDOWN_SENT_KEY = 'avTelemetry.teardownSentIds';

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private deviceCode = '';
  private lastSevereUploadAt = 0;
  private inFlight: Promise<AvTelemetryUploadResult> | null = null;
  private listenersBound = false;
  private rowsWrittenSubscription: Subscription | null = null;

  /** Kept warm because `pagehide` cannot await `acquireTokenSilent`. */
  private teardownHeaders: Record<string, string> | null = null;

  /**
   * Kept warm for the same reason: once `pagehide` fires the browser never resumes the page's
   * pending async work, so reading IndexedDB or gzipping there means fetch is never reached.
   */
  private teardownSnapshot: TeardownBatch[] | null = null;
  private snapshotGeneration = 0;

  /** Rows a cadence POST has put on the wire; teardown must not ship them a second time. */
  private readonly dispatchedIds = new Set<number>();

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      void this.upload();
    }
  };

  private readonly onPageHide = (): void => {
    this.sendTeardownSnapshot();
  };

  constructor(
    private readonly http: HttpClient,
    private readonly bufferStore: AvTelemetryBufferStore,
    private readonly store: Store,
    private readonly msalService: MsalService,
    @Inject(AV_TELEMETRY_UPLOAD_CONFIG) private readonly config: AvTelemetryUploadConfig
  ) {}

  ngOnDestroy(): void {
    void this.stop();
  }

  /** Orphan pickup is deliberately not wired here — it belongs to 298375. */
  async start(deviceCode: string): Promise<void> {
    if (this.intervalId !== null) {
      return;
    }

    this.deviceCode = deviceCode ?? '';

    this.intervalId = setInterval(() => void this.upload(), this.config.uploadIntervalMs);
    this.bindTeardownListeners();
    this.rowsWrittenSubscription = this.bufferStore.rowsWritten$.subscribe(
      () => void this.rebuildTeardownSnapshot()
    );

    await this.consumeTeardownSentIds();

    // Covers a session that ends before the first cadence tick.
    await this.refreshTeardownHeaders();
    await this.rebuildTeardownSnapshot();
  }

  async stop(): Promise<void> {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.unbindTeardownListeners();
    this.rowsWrittenSubscription?.unsubscribe();
    this.rowsWrittenSubscription = null;

    await this.upload();
  }

  async uploadForEvent(eventName: string): Promise<AvTelemetryUploadResult> {
    if (!this.config.severeEventNames.includes(eventName)) {
      return { outcome: 'skipped', rowsUploaded: 0 };
    }

    const now = Date.now();
    if (now - this.lastSevereUploadAt < this.config.severeDebounceMs) {
      return { outcome: 'skipped', rowsUploaded: 0 };
    }

    const result = await this.upload();

    // A skipped run never reached the network, so it must not suppress a genuine severe event
    // moments later. Everything else consumes the window — including a failure, because severe
    // events cluster during the same degradation that makes POSTs fail, and exempting them would
    // turn one outage into a POST per event. The 60s cadence still re-ships buffered rows.
    if (result.outcome !== 'skipped') {
      this.lastSevereUploadAt = now;
    }

    return result;
  }

  /** Serialised: concurrent triggers would otherwise read the same rows and POST duplicates. */
  async upload(): Promise<AvTelemetryUploadResult> {
    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.runUpload().finally(() => {
      this.inFlight = null;
      void this.rebuildTeardownSnapshot();
    });

    return this.inFlight;
  }

  private async runUpload(): Promise<AvTelemetryUploadResult> {
    // BaseUrlInterceptor interpolates the store value, so an unset package sends "undefined"
    // verbatim and ingest 400s.
    if (!(await this.isIdentityHydrated())) {
      return { outcome: 'skipped', rowsUploaded: 0 };
    }

    const rows = await this.readShippableRows();
    if (!rows.length) {
      return { outcome: 'nothingToUpload', rowsUploaded: 0 };
    }

    let uploaded = 0;
    let hadPermanentFailure = false;

    for (const batch of this.batchBySessionKey(rows)) {
      const body = await this.gzip(this.toNdjson(batch));
      if (!body) {
        return { outcome: 'failed', rowsUploaded: uploaded };
      }

      // Held until the rows are deleted, not just until the POST returns: a snapshot rebuilt in
      // between could still read them from the store.
      const ids = this.idsOf(batch);
      ids.forEach((id) => this.dispatchedIds.add(id));

      const postOutcome = await this.post(body);

      if (postOutcome === 'transientFailure') {
        ids.forEach((id) => this.dispatchedIds.delete(id));
        return { outcome: 'failed', rowsUploaded: uploaded };
      }

      this.invalidateTeardownSnapshot();

      // A 422 chunk will never be accepted, so it is discarded like a sent one — but unlike a
      // transient failure it says nothing about backend health, so unrelated batches still get a try.
      // Deleting before the response confirms would discard telemetry that never landed.
      await this.bufferStore.deleteByIds(ids);
      ids.forEach((id) => this.dispatchedIds.delete(id));

      if (postOutcome === 'permanentFailure') {
        hadPermanentFailure = true;
        continue;
      }

      uploaded += batch.length;
    }

    // Not awaited: this only maintains the teardown cache, and callers of upload() should not
    // block on an MSAL round trip that has no bearing on the result.
    void this.refreshTeardownHeaders();

    return { outcome: hadPermanentFailure ? 'failed' : 'uploaded', rowsUploaded: uploaded };
  }

  /**
   * Snapshots what BaseUrlInterceptor would otherwise add. A failure keeps the previous snapshot:
   * clearing it would disable teardown flushing after one transient MSAL hiccup.
   */
  private async refreshTeardownHeaders(): Promise<void> {
    try {
      const [packageGuid, packageUserGuid, packageId, deviceCode, idTokenClaims] = await firstValueFrom(
        combineLatest([
          this.store.pipe(select(PackagesSelectors.getActivePackageGuid)),
          this.store.pipe(select(PackageUsersSelectors.getActivePackageUserGuid)),
          this.store.pipe(select(PackagesSelectors.getActivePackageId)),
          this.store.pipe(select(DeviceGroupSelectors.getDeviceCode)),
          this.store.pipe(select(MsalAuthSelectors.getIdTokenClaims)),
        ]).pipe(first())
      );

      const resolvedPackageGuid = packageGuid ?? idTokenClaims?.extension_PackageGuid;

      const token = await this.acquireAccessToken();
      if (!token || !resolvedPackageGuid || !packageUserGuid || !packageId) {
        return;
      }

      this.teardownHeaders = {
        Authorization: `Bearer ${token}`,
        ...buildCorrelationHeaders({
          packageGuid,
          packageUserGuid,
          packageId,
          deviceCode: deviceCode ?? this.deviceCode,
          idTokenClaims,
        }),
      };
    } catch {
      // Telemetry must never disrupt the session it observes.
    }
  }

  /**
   * Uses MSAL's default authority, unlike MSALInterceptorConfigFactory which reads the account's
   * `acr`/`tfp` claim. May differ for secure-link and signing-agent logins.
   */
  private async acquireAccessToken(): Promise<string | null> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      return null;
    }

    const result = await firstValueFrom(
      this.msalService.acquireTokenSilent({
        account: account,
        scopes: environment.consentScopes,
      })
    );

    return result?.accessToken ?? null;
  }

  /**
   * Must stay synchronous end to end: the page is unloading, so any `await` before fetch would
   * never resume. Everything async was done ahead of time by rebuildTeardownSnapshot().
   *
   * Does not defer to an in-flight cadence upload. On a real unload that upload dies at its own
   * first `await`, so deferring to it would ship nothing — only rows it has already put on the
   * wire are skipped.
   */
  private sendTeardownSnapshot(): void {
    const headers = this.teardownHeaders;
    const snapshot = this.teardownSnapshot;
    if (!headers || !snapshot?.length) {
      return;
    }

    const batches = snapshot.filter((batch) => !batch.ids.some((id) => this.dispatchedIds.has(id)));
    if (!batches.length) {
      return;
    }

    // At-most-once: the page cannot see whether a keepalive landed, so without this record the
    // next session would re-ship these rows and compaction would count them twice. Losing a chunk
    // to a failed keepalive is the lesser harm, so no record means no send.
    if (!this.recordTeardownSentIds(batches.flatMap((batch) => batch.ids))) {
      return;
    }

    batches.forEach((batch) => this.sendKeepalive(batch.body, headers));
    this.invalidateTeardownSnapshot();
  }

  /** Superseded rebuilds are discarded via the generation, so a slow one cannot overwrite a newer result. */
  private async rebuildTeardownSnapshot(): Promise<void> {
    this.snapshotGeneration++;
    const generation = this.snapshotGeneration;

    try {
      const batches = (await this.isIdentityHydrated())
        ? await this.buildTeardownBatches(
            (await this.readShippableRows()).filter((row) => !this.dispatchedIds.has(row.id))
          )
        : null;

      if (generation === this.snapshotGeneration) {
        this.teardownSnapshot = batches;
      }
    } catch {
      // Telemetry must never disrupt the session it observes.
    }
  }

  private invalidateTeardownSnapshot(): void {
    this.snapshotGeneration++;
    this.teardownSnapshot = null;
  }

  /**
   * One sessionKey per body (ingest files a chunk under its first line's session), and the total
   * stays under maxKeepaliveBytes because the browser caps keepalive across all in-flight requests.
   * Newest batches go first: older rows are leftovers the next session's cadence will ship anyway.
   */
  private async buildTeardownBatches(rows: readonly AvTelemetryBufferRow[]): Promise<TeardownBatch[]> {
    const batches: TeardownBatch[] = [];
    let remaining = this.config.maxKeepaliveBytes;

    for (const group of this.batchBySessionKey(rows).reverse()) {
      for (const batch of await this.fitWithinBudget(group, remaining)) {
        batches.push(batch);
        remaining -= batch.body.size;
      }
    }

    return batches;
  }

  /** Halves an oversized batch until it fits, so one large session does not exclude the rest. */
  private async fitWithinBudget(rows: readonly AvTelemetryBufferRow[], budget: number): Promise<TeardownBatch[]> {
    if (!rows.length || budget <= 0) {
      return [];
    }

    const body = await this.gzip(this.toNdjson(rows));
    if (!body) {
      return [];
    }

    if (body.size <= budget) {
      return [{ body: body, ids: this.idsOf(rows) }];
    }

    if (rows.length === 1) {
      return [];
    }

    const middle = Math.ceil(rows.length / 2);
    const newer = await this.fitWithinBudget(rows.slice(middle), budget);
    const newerBytes = newer.reduce((total, batch) => total + batch.body.size, 0);

    return [...newer, ...(await this.fitWithinBudget(rows.slice(0, middle), budget - newerBytes))];
  }

  private recordTeardownSentIds(ids: readonly number[]): boolean {
    try {
      const merged = [...new Set([...this.readTeardownSentIds(), ...ids])];
      localStorage.setItem(AvTelemetryUploaderService.TEARDOWN_SENT_KEY, JSON.stringify(merged));

      return true;
    } catch {
      return false;
    }
  }

  private readTeardownSentIds(): number[] {
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(AvTelemetryUploaderService.TEARDOWN_SENT_KEY) ?? '[]');

      return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === 'number') : [];
    } catch {
      return [];
    }
  }

  /** Deletes what the previous page's teardown shipped but could not delete itself. */
  private async consumeTeardownSentIds(): Promise<void> {
    const ids = this.readTeardownSentIds();
    if (!ids.length) {
      return;
    }

    await this.bufferStore.deleteByIds(ids);

    try {
      localStorage.removeItem(AvTelemetryUploaderService.TEARDOWN_SENT_KEY);
    } catch {
      // A leftover record is harmless: readShippableRows() keeps excluding those ids.
    }
  }

  /**
   * Reads the current value rather than waiting: a parked upload would outlive its trigger.
   *
   * Mirrors BaseUrlInterceptor's own fallback to the id-token claim, so this gate never blocks an
   * upload the interceptor would have sent a valid header for.
   */
  private async isIdentityHydrated(): Promise<boolean> {
    const [packageGuid, claimPackageGuid] = await firstValueFrom(
      combineLatest([
        this.store.pipe(select(PackagesSelectors.getActivePackageGuid)),
        this.store.pipe(select(MsalAuthSelectors.getPackageGuid)),
      ]).pipe(first())
    );

    return !!(packageGuid ?? claimPackageGuid);
  }

  private async readShippableRows(): Promise<AvTelemetryBufferRow[]> {
    const rows = await this.bufferStore.readAll();

    // Shipped by a teardown that could not delete them. Excluded until start() does, which also
    // covers a page restored from the back/forward cache after its pagehide already sent them.
    const teardownSentIds = new Set(this.readTeardownSentIds());

    // Without an id there is nothing to delete after a successful POST. Rows buffered before
    // sessionKey existed (IndexedDB persists across releases) have no sessionKey, and an empty
    // one is a guaranteed 400 at ingest — the uploader treats 400 as transient, so a row like
    // this would otherwise jam the queue at the head forever.
    return rows.filter(
      (row) => typeof row.id === 'number' && !!row.envelope.sessionKey && !teardownSentIds.has(row.id)
    );
  }

  private idsOf(rows: readonly AvTelemetryBufferRow[]): number[] {
    return rows.map((row) => row.id).filter((id): id is number => typeof id === 'number');
  }

  private batch(rows: readonly AvTelemetryBufferRow[]): AvTelemetryBufferRow[][] {
    const batches: AvTelemetryBufferRow[][] = [];

    for (let index = 0; index < rows.length; index += this.config.maxRowsPerBatch) {
      batches.push(rows.slice(index, index + this.config.maxRowsPerBatch));
    }

    return batches;
  }

  /**
   * Ingest peeks only the first NDJSON line's `sessionKey` and files the whole chunk under the
   * package it resolves to — a batch spanning two sessions would misattribute every row after
   * the first. Grouping before chunking keeps every batch single-session.
   */
  private batchBySessionKey(rows: readonly AvTelemetryBufferRow[]): AvTelemetryBufferRow[][] {
    const bySessionKey = new Map<string, AvTelemetryBufferRow[]>();

    for (const row of rows) {
      const sessionKey = row.envelope.sessionKey;
      const group = bySessionKey.get(sessionKey);

      if (group) {
        group.push(row);
      } else {
        bySessionKey.set(sessionKey, [row]);
      }
    }

    return [...bySessionKey.values()].flatMap((group) => this.batch(group));
  }

  private toNdjson(rows: readonly AvTelemetryBufferRow[]): string {
    return rows.map((row) => JSON.stringify(row.envelope)).join('\n') + '\n';
  }

  private async post(body: Blob): Promise<PostOutcome> {
    try {
      await firstValueFrom(
        this.http.post(AvTelemetryUploaderService.INGEST_ROUTE, body, {
          headers: { 'Content-Encoding': 'gzip', 'Content-Type': 'application/x-ndjson' },
        })
      );

      return 'sent';
    } catch (err) {
      return err instanceof HttpErrorResponse && err.status === 422 ? 'permanentFailure' : 'transientFailure';
    }
  }

  // Seams: CompressionStream and fetch are host globals, so the specs override these.

  protected async gzip(payload: string): Promise<Blob | null> {
    try {
      const stream = new Blob([payload]).stream().pipeThrough(new CompressionStream('gzip'));

      return await new Response(stream).blob();
    } catch {
      return null;
    }
  }

  /**
   * keepalive survives the page but bypasses the interceptors, so headers are set by hand.
   * Fire-and-forget: the page will not live to read the response.
   */
  protected sendKeepalive(body: Blob, headers: Record<string, string>): void {
    // Telemetry must never disrupt the session it observes; fetch reports every failure as a rejection.
    fetch(`${environment.apiBaseUrl}${AvTelemetryUploaderService.INGEST_ROUTE}`, {
      method: 'POST',
      keepalive: true,
      headers: {
        ...headers,
        'Content-Encoding': 'gzip',
        'Content-Type': 'application/x-ndjson',
      },
      body: body,
    }).catch(() => undefined);
  }

  private bindTeardownListeners(): void {
    if (this.listenersBound) {
      return;
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('pagehide', this.onPageHide);
    this.listenersBound = true;
  }

  private unbindTeardownListeners(): void {
    if (!this.listenersBound) {
      return;
    }

    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pagehide', this.onPageHide);
    this.listenersBound = false;
  }
}
